// Feeds real data into the page layer's L.state: indexer snapshot + live events + the wallet's own reads.
// Shapes match the prototype's (USD prices, coin/ipo/vote objects) so the pages render unchanged,
// with extra fields (address, priceEth, ipoId, numeric vote ids) for transactions.
import type { Address } from 'viem';
import * as chain from './chain';
import { config } from './config';

declare global {
  interface Window {
    L: any;
    LC: any;
    LIVE: typeof LIVE;
  }
}

const DAY = 864e5;
// ListedHook.EARNINGS_PERIOD = 90 protocol days (real days unless this is a fast testnet rehearsal).
const EARNINGS_PERIOD = 90 * (Number(config.deployment?.day) || 86_400) * 1000;
const api = (p: string) => `${config.indexerUrl}/api${p}`;
const L = () => window.L;

let raw: any = null; // last /api/state
let ethUsd = 2500;
let account: any = null;
let refreshing: Promise<void> | null = null;
let lastBlock = 0;
let chainOffset = 0; // chain time minus browser time, from the indexer
const candleCache: Record<string, { at: number; pts: [number, number][] }> = {};

function hue(s: string) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) % 360;
}

const bellWhen = (eta: number) => {
  const d = eta - Date.now();
  if (d <= 0) return 'READY';
  if (d < DAY) return 'IN ' + Math.ceil(d / 36e5) + 'H';
  return new Date(eta).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' }).toUpperCase();
};

function mapCoin(c: any) {
  const prev = L().coin(c.sym);
  const created = c.created;
  const lastE = c.lastEarnings?.ts ?? 0;
  return Object.assign(prev && prev.address === c.address ? prev : {}, {
    sym: c.sym,
    name: c.name,
    desc: c.desc,
    price: c.price * ethUsd,
    p24: (c.p24 || c.price) * ethUsd,
    supply: c.supply,
    vol: c.vol24 * ethUsd,
    holders: c.holders,
    treasury: c.treasury * ethUsd,
    feesEarned: c.creatorEarned * ethUsd,
    creatorFee: c.creatorFee,
    walletShare: c.walletShare,
    creator: c.creator,
    created,
    site: c.site,
    x: c.x,
    tg: c.tg,
    logo: c.logo ? config.indexerUrl + c.logo : null,
    hue: hue(c.address),
    contract: c.address,
    address: c.address,
    action: c.action ? { ...c.action, when: bellWhen(c.action.eta) } : null,
    nextEarnings: Math.max(created, lastE) + EARNINGS_PERIOD,
    lastEarnings: c.lastEarnings,
    mode: c.mode,
    ipoId: c.ipoId,
    launchTax: c.mode === 'instant' ? created : 0,
    buybacks: c.buybacks * ethUsd,
    burned: c.burned,
    dividends: c.dividends * ethUsd,
    splits: c.splits,
    priceEth: c.price,
    volume: c.volume * ethUsd,
    trades: c.trades,
    creatorAccruedEth: c.creatorAccrued,
    fired: c.fired,
    lockedUntil: c.lockedUntil || 0,
    recurring: c.recurring,
    grants: c.grants || [],
    boosts: c.boosts || [],
    treasuryEth: c.treasury,
  });
}

function mapIpo(i: any) {
  return {
    id: i.id,
    token: i.token,
    sym: i.sym,
    name: i.name,
    desc: i.desc,
    raised: i.raised,
    min: i.min,
    bidders: i.bidders,
    bell: i.bell,
    auctionSupply: i.auctionSupply,
    supply: i.supply,
    maxPerWallet: i.maxPerWallet,
    status: i.status,
    creator: i.creator,
    hue: hue(i.token),
    logo: i.logo ? config.indexerUrl + i.logo : null,
    creatorFee: i.creatorFee,
    walletShare: i.walletShare,
    site: i.site,
    x: i.x,
    tg: i.tg,
    created: i.created,
    clearingPrice: i.clearingPrice * ethUsd, // USD per token
    clearingPriceEth: i.clearingPrice,
    finalRaised: i.finalRaised,
  };
}

const SUPPORT = ['against', 'for', 'abstain'];

function voteText(v: any, coinName: string) {
  const p = v.params || {};
  switch (v.type) {
    case 'split':
      return { title: `Split ${p.num}-for-${p.den}`, desc: `Every balance is multiplied by ${p.num / p.den} and the price divided by the same amount. Market cap stays the same.`, params: { ratio: p.num / p.den, num: p.num, den: p.den } };
    case 'reverse':
      return { title: `Reverse split ${p.num}-for-${p.den}`, desc: `Every ${p.den} coins become ${p.num}. The price goes up by ${p.den / p.num}x and market cap stays the same.`, params: { ratio: p.num / p.den, num: p.num, den: p.den } };
    case 'buyback':
      return { title: `Buy back and burn ${p.eth} ETH of $${v.sym}`, desc: `The treasury buys $${v.sym} on the market (max ${(p.maxImpactBps ?? 300) / 100}% price impact, measured from the 30-minute average price) and burns every coin it buys, so the supply shrinks for good. Unspent ETH stays in the treasury. It only runs while the price is within about 2% of that average, so if the price just moved sharply it waits and the keeper tries again.`, params: { eth: p.eth, usd: p.eth * ethUsd, maxImpactPct: (p.maxImpactBps ?? 300) / 100 } };
    case 'fire':
      return { title: `Fire the CEO of $${v.sym}`, desc: `The creator is paid the fees they already earned (if their wallet can't take ETH, the fees stay theirs to claim later), then removed for good. Their share of the creator fee goes to the treasury from then on, and nobody replaces them. Their coins are not touched. Needs 25% turnout and two thirds yes.`, params: {} };
    case 'recurring':
      return p.payouts
        ? { title: `Pay ${p.eth} ETH to holders every ${p.intervalDays === 7 ? 'week' : '30 days'}, ${p.payouts} times`, desc: `A dividend paid automatically from the treasury: the first with the vote, then one every ${p.intervalDays === 7 ? '7' : '30'} days, ${p.payouts} in all (${+(p.eth * p.payouts).toFixed(6)} ETH). Each payout is shared by holders' average holding over the 7 days before it. If the treasury runs short, a payout pays what is there. A new vote can stop it.`, params: { eth: p.eth, usd: p.eth * ethUsd, intervalDays: p.intervalDays, payouts: p.payouts } }
        : { title: `Stop the recurring dividend`, desc: `No more scheduled payouts. ETH stays in the treasury.`, params: { payouts: 0 } };
    case 'lock':
      return { title: `Lock the treasury until ${new Date(p.until).toUTCString().slice(5, 16)}`, desc: `Nothing can be spent from the treasury until then: no buybacks, dividends, grants or boosts. Fees keep flowing in. A lock can be extended later, never shortened. Needs 20% turnout.`, params: { until: p.until } };
    case 'grant':
      return { title: `Grant ${p.eth} ETH to ${String(p.to).slice(0, 6)}…${String(p.to).slice(-4)}`, desc: `Sends treasury ETH to that wallet${p.vestingDays ? `, unlocking evenly over ${p.vestingDays} days` : ' at once'}. Capped at 10% of the treasury and one grant per 30 days. Needs 20% turnout and 60% yes.`, params: { eth: p.eth, usd: p.eth * ethUsd, to: p.to, vestingDays: p.vestingDays } };
    case 'boost':
      return { title: `Add ${p.eth} ETH of liquidity`, desc: `Treasury ETH becomes a buy wall from just under today's price down to half of it, so sells move the price less. The ETH is locked in the pool forever. At least 0.000001 ETH and at most 25% of the treasury. It waits if the price just moved sharply.`, params: { eth: p.eth, usd: p.eth * ethUsd } };
    case 'dividend':
      return { title: `Pay a ${p.eth} ETH dividend`, desc: `Paid from the treasury to $${v.sym} holders, pro rata to each one's average holding over the 7 days before the payout. Coins bought right before it earn almost nothing. Holders claim it on the coin page.`, params: { eth: p.eth, usd: p.eth * ethUsd } };
    case 'rebrand':
      return { title: `Rebrand to ${p.name} ($${p.symbol})`, desc: `Same coin, same holders and contract. ${coinName} becomes ${p.name}.`, params: { name: p.name, sym: p.symbol } };
    default:
      return { title: `Set the creator fee to ${(p.creatorBps ?? 0) / 100}%`, desc: `${Math.round((p.walletShareBps ?? 0) / 100)}% of it to the creator's wallet, the rest to the treasury.`, params: { fee: (p.creatorBps ?? 0) / 100, walletShare: (p.walletShareBps ?? 0) / 10000 } };
  }
}

function mapVote(v: any) {
  const coin = L().coin(v.sym);
  const t = voteText(v, coin?.name ?? v.sym);
  const mine = account?.votes?.find((x: any) => x.id === v.id);
  return {
    id: 'v' + v.id,
    num: v.id,
    token: v.token,
    sym: v.sym,
    type: v.type,
    title: t.title,
    params: t.params,
    raw: v.params,
    desc: t.desc,
    for: v.for,
    against: v.against,
    abstain: v.abstain,
    quorum: v.quorum,
    approval: v.approval ?? 0.5,
    strict: !!v.strict,
    totalPower: v.totalPower,
    ends: v.ends,
    eta: v.eta,
    status: v.status === 'cancelled' || v.status === 'expired' ? 'failed' : v.status,
    rawStatus: v.status,
    proposer: v.proposer,
    created: v.created,
    voters: v.voters,
    myVote: mine ? SUPPORT[mine.support] : null,
  };
}

function mapTrade(t: any, me: string | null) {
  return {
    sym: t.sym,
    side: t.side,
    usd: t.eth * ethUsd,
    eth: t.eth,
    qty: t.qty,
    price: t.price * ethUsd,
    addr: t.trader,
    ts: t.ts,
    mine: !!me && t.trader.toLowerCase() === me,
    tx: t.tx,
    kind: t.kind,
  };
}

function applyState() {
  const s = L().state;
  if (!raw || !s) return;
  ethUsd = raw.ethUsd || ethUsd;
  L().ETH_USD = ethUsd;
  const me = chain.currentWallet()?.address.toLowerCase() ?? null;
  s.coins = raw.coins.map(mapCoin);
  s.ipos = raw.ipos.map(mapIpo);
  s.votes = raw.votes.map(mapVote);
  s.trades = raw.trades.map((t: any) => mapTrade(t, me));
  s.platform = { ...raw.platform, feesUsd: raw.platform.fees * ethUsd, volumeUsd: raw.platform.volume * ethUsd };
  s.listed = Object.assign(s.listed || {}, { revenue: raw.platform.fees * ethUsd });
  s.site = raw.site ?? { listed: null, admin: false };
  s.block = raw.block;
  if (raw.chainTime) chainOffset = raw.chainTime - Date.now();
}

async function applyAccount() {
  const s = L().state;
  const w = chain.currentWallet();
  if (!w) {
    account = null;
    s.wallet = null;
    s.holdings = {};
    s.bids = [];
    s.myCoins = [];
    return;
  }
  const [acct, eth] = await Promise.all([fetch(api(`/accounts/${w.address}`)).then((r) => r.json()), chain.ethBalance(w.address)]);
  account = acct;
  s.wallet = { addr: w.address, name: w.name, eth, connected: s.wallet?.connected ?? Date.now() };
  const holdings: Record<string, any> = {};
  const divs = await Promise.all(acct.holdings.map((h: any) => chain.claimableDividend(h.token, w.address).catch(() => 0)));
  acct.holdings.forEach((h: any, i: number) => {
    holdings[h.sym] = { qty: h.qty, cost: h.cost * ethUsd, costEth: h.cost, proceeds: h.proceeds * ethUsd, proceedsEth: h.proceeds, div: divs[i] * ethUsd, divEth: divs[i], token: h.token };
  });
  s.holdings = holdings;
  s.bids = acct.bids.map((b: any) => {
    const ipo = s.ipos.find((i: any) => i.id === b.ipoId);
    const status = b.amount === 0 ? 'cancelled' : ipo?.status === 'priced' ? (b.claimed ? 'claimed' : 'won') : ipo?.status === 'refunded' || ipo?.status === 'cancelled' ? (b.claimed ? 'refunded' : 'refund') : 'open';
    return { id: 'b' + b.ipoId, ipoId: b.ipoId, sym: b.sym, eth: b.amount, level: b.level, claimed: b.claimed, status, ts: ipo?.created ?? 0 };
  });
  s.myCoins = acct.created;
  s.myTrades = acct.trades.map((t: any) => mapTrade(t, w.address.toLowerCase()));
  s.activity = acct.activity;
  s.votes.forEach((v: any) => {
    const mine = acct.votes.find((x: any) => x.id === v.num);
    v.myVote = mine ? SUPPORT[mine.support] : null;
  });
}

async function doRefresh() {
  raw = await fetch(api('/state')).then((r) => {
    if (!r.ok) throw new Error('indexer ' + r.status);
    return r.json();
  });
  applyState();
  await applyAccount().catch((e) => console.warn('[live] account', e));
  L().emit('state', L().state);
}

/** Pull everything again. Cheap; called after every confirmed transaction and on new blocks. */
function refresh(): Promise<void> {
  if (!refreshing)
    refreshing = doRefresh()
      .catch((e) => {
        console.warn('[live] refresh', e);
        LIVE.offline = true;
      })
      .finally(() => {
        refreshing = null;
      });
  return refreshing;
}

/** Waits until the indexer has seen `block` (so a page re-render shows the tx's effect). */
async function waitIndexed(block: bigint | number, timeoutMs = 15_000) {
  const target = Number(block);
  const t0 = Date.now();
  while (Date.now() - t0 < timeoutMs) {
    const h = await fetch(api('/health')).then((r) => r.json()).catch(() => null);
    if (h && h.block >= target) break;
    await new Promise((r) => setTimeout(r, 500));
  }
  await refresh();
}

// ------------------------------------------------------------------ price history for L.chart / L.spark

const RANGE_TTL = 15_000;
function history(sym: string, range: string): [number, number][] {
  const c = L().coin(sym);
  if (!c) return [];
  const key = c.address + ':' + range;
  const hit = candleCache[key];
  if (!hit || Date.now() - hit.at > RANGE_TTL) {
    candleCache[key] = { at: Date.now(), pts: hit?.pts ?? [] };
    fetch(api(`/coins/${c.address}/candles?range=${range}`))
      .then((r) => r.json())
      .then((ks: number[][]) => {
        candleCache[key] = { at: Date.now(), pts: ks.map((k) => [k[0], k[4] * ethUsd]) };
      })
      .catch(() => {});
  }
  const now = Date.now();
  const pts = (candleCache[key].pts || []).slice();
  const start = range === '1H' ? now - 36e5 : range === '1D' ? now - DAY : range === '1W' ? now - 7 * DAY : c.created;
  if (!pts.length || pts[0][0] > Math.max(start, c.created)) pts.unshift([Math.max(start, c.created), pts.length ? pts[0][1] : c.p24 || c.price]);
  pts.push([now, c.price]);
  return pts;
}

// ------------------------------------------------------------------ notifications from live events

function notifyFor(evt: string, data: any) {
  const s = L().state;
  const me = chain.currentWallet()?.address.toLowerCase();
  if (!me) return;
  if (evt === 'ipo') {
    const i = raw?.ipos.find((x: any) => x.id === data.id);
    const bid = s.bids?.find((b: any) => b.ipoId === data.id);
    if (i && (bid || i.creator === me) && i.status !== 'open') {
      const text = i.status === 'priced' ? `$${i.sym} priced at the bell. Claim your coins on the IPO page.` : `$${i.sym} did not reach its minimum. Your ETH is ready to claim back.`;
      L().notify(text, { kind: 'ipo', go: 'ipo-' + i.sym });
    }
  }
  if (evt === 'vote') {
    const v = raw?.votes.find((x: any) => x.id === data.id);
    if (v && s.holdings?.[v.sym] && v.status === 'open' && Date.now() - v.created < 120_000)
      L().notify(`$${v.sym} holders are voting: ${voteText(v, v.sym).title}.`, { kind: 'vote', go: 'vote-v' + v.id });
  }
}

function connectEvents() {
  let es: EventSource | null = null;
  let retry = 1000;
  const open = () => {
    es = new EventSource(api('/events'));
    es.addEventListener('hello', () => {
      retry = 1000;
      LIVE.offline = false;
    });
    es.addEventListener('block', (e: MessageEvent) => {
      const b = JSON.parse(e.data).block;
      if (b > lastBlock) {
        lastBlock = b;
        refresh();
      }
    });
    es.addEventListener('trade', (e: MessageEvent) => {
      const t = mapTrade(JSON.parse(e.data), chain.currentWallet()?.address.toLowerCase() ?? null);
      // The block event that follows refreshes state; this emits the trade for live tapes right away.
      setTimeout(() => L().emit('trade', t), 0);
    });
    es.addEventListener('site', (e: MessageEvent) => {
      const site = JSON.parse(e.data);
      if (raw) raw.site = site;
      if (L().state) L().state.site = site;
      L().emit('site', site);
    });
    es.addEventListener('listing', () => refresh().then(() => L().emit('listing', L().state.coins[0])));
    for (const k of ['ipo', 'vote', 'split'])
      es.addEventListener(k, (e: MessageEvent) => {
        const d = JSON.parse(e.data);
        refresh().then(() => notifyFor(k, d));
      });
    es.onerror = () => {
      LIVE.offline = true;
      es?.close();
      setTimeout(open, (retry = Math.min(retry * 2, 30_000)));
    };
  };
  open();
}

export const LIVE = {
  offline: false,
  /** Best estimate of the chain's current time in ms (use it, not Date.now(), to compare with bells and vote ends). */
  chainNow: () => Date.now() + chainOffset,
  refresh,
  waitIndexed,
  history,
  get ethUsd() {
    return ethUsd;
  },
  /** Coin summary + split list + earnings for one coin. */
  coinDetail: (addr: string) => fetch(api(`/coins/${addr}`)).then((r) => r.json()),
  coinTrades: (addr: string) => fetch(api(`/coins/${addr}/trades`)).then((r) => r.json()).then((ts: any[]) => ts.map((t) => mapTrade(t, chain.currentWallet()?.address.toLowerCase() ?? null))),
  coinHolders: (addr: string) => fetch(api(`/coins/${addr}/holders`)).then((r) => r.json()),
  voteDetail: (num: number) => fetch(api(`/votes/${num}`)).then((r) => r.json()),
  accountOf: (addr: Address) => fetch(api(`/accounts/${addr}`)).then((r) => r.json()),
  ipoDetail: (id: number) => fetch(api(`/ipos/${id}`)).then((r) => r.json()),
  stats: () => fetch(api('/stats')).then((r) => r.json()),
  leaders: () => fetch(api('/leaders')).then((r) => r.json()),
  async start() {
    await refresh();
    lastBlock = raw?.block ?? 0;
    connectEvents();
    chain.onWallet(() => refresh());
  },
};
